import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toggleMenu, selectIsMenuOpen, setMenuOpen } from '../../store/slices/uiSlice';
import { useScrollPosition } from '../../hooks';
import { navigationLinks } from '../../data/siteData';

const Navbar = () => {
  const dispatch = useDispatch();
  const isMenuOpen = useSelector(selectIsMenuOpen);
  const { scrollY } = useScrollPosition();
  const location = useLocation();

  const handleToggleMenu = () => {
    dispatch(toggleMenu());
  };

  const handleCloseMenu = () => {
    dispatch(setMenuOpen(false));
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrollY > 100
          ? 'bg-black/90 backdrop-blur-lg py-3'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" onClick={handleCloseMenu}>
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-400 flex items-center justify-center font-gaming font-bold text-xl">
            RB
          </div>
          <span className="font-gaming text-2xl tracking-wider hidden sm:block">
            <span className="text-primary-500">RIVEN</span>
            <span className="text-secondary-400">BYTE</span>
          </span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-8">
          {navigationLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`text-sm font-medium tracking-wide transition-colors relative group link-underline ${
                isActive(link.path) ? 'text-primary-500' : 'hover:text-primary-400'
              }`}
            >
              {link.name.toUpperCase()}
            </Link>
          ))}
          <Link
            to="/play"
            className="px-6 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-semibold text-sm hover:from-primary-400 hover:to-primary-500 transition-all transform hover:scale-105"
          >
            PLAY NOW
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={handleToggleMenu}
          className="lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 z-50"
          aria-label="Toggle menu"
        >
          <span
            className={`w-6 h-0.5 bg-white transition-all duration-300 ${
              isMenuOpen ? 'rotate-45 translate-y-2' : ''
            }`}
          />
          <span
            className={`w-6 h-0.5 bg-white transition-all duration-300 ${
              isMenuOpen ? 'opacity-0' : ''
            }`}
          />
          <span
            className={`w-6 h-0.5 bg-white transition-all duration-300 ${
              isMenuOpen ? '-rotate-45 -translate-y-2' : ''
            }`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/98 backdrop-blur-lg transition-all duration-500 ${
          isMenuOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        style={{ top: '80px' }}
      >
        <div className="p-6 flex flex-col gap-4 max-w-7xl mx-auto">
          {navigationLinks.map((link, index) => (
            <Link
              key={link.name}
              to={link.path}
              onClick={handleCloseMenu}
              className={`text-2xl font-medium py-4 border-b border-white/10 hover:text-primary-400 transition-all ${
                isActive(link.path) ? 'text-primary-500' : ''
              }`}
              style={{
                animationDelay: `${index * 50}ms`,
              }}
            >
              {link.name}
            </Link>
          ))}
          <Link
            to="/play"
            onClick={handleCloseMenu}
            className="mt-4 w-full px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl font-bold text-lg text-center hover:from-primary-400 hover:to-primary-500 transition-all"
          >
            PLAY NOW
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
